import React from 'react';

export interface StageBoundaryProps {
  /**
   * Changing this clears a caught error. It is the row key plus the source's identity, so a new
   * selection, a rewritten artifact and `Try again` all reset the subtree, and a re-render for any
   * other reason does not put a broken renderer back on the stage.
   */
  resetKey: string;
  /** Records the failure, which is what turns the pane's own `failed` board on. */
  onError: (error: Error) => void;
  /** What to draw in the artifact's place while the caught error stands. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

interface StageBoundaryState {
  error: Error | null;
  resetKey: string;
}

/**
 * One artifact's blast radius.
 *
 * The stage mounts arbitrary content — a model-authored document, a scraped page, somebody's photo —
 * and a throw anywhere in it would otherwise take the whole route down, including the directory that
 * is the only way to open something else. So each renderer gets one of these.
 *
 * The fallback it draws is deliberately plain, and it is NOT the pane's failed board. That board is
 * `PreviewPane`'s, it is drawn INSTEAD of the artifact rather than inside it, and it is reached by the
 * pane's `status` — which the host computed before this component existed. `onError` records the
 * failure in `stageSignals`, so a host reading `useStageFor` gets the real board on the next render and
 * this fallback is never seen; a host reading the plain `stageFor` sees this until something else makes
 * it re-render. Either way the route survives, which is the part that matters.
 */
export class StageBoundary extends React.Component<StageBoundaryProps, StageBoundaryState> {
  constructor(props: StageBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<StageBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: StageBoundaryProps,
    state: StageBoundaryState,
  ): Partial<StageBoundaryState> | null {
    // Runs on every render, INCLUDING the one right after `getDerivedStateFromError` — the key is
    // unchanged there, so the error survives its own re-render and only a real reset clears it.
    if (props.resetKey !== state.resetKey) return { error: null, resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error): void {
    this.props.onError(error);
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 48,
          boxSizing: 'border-box',
          textAlign: 'center',
          // The artifact's own coordinate space is 1920 wide and the stage fits it to roughly half
          // that, so a 14px message would arrive at 7. Sized for where it is actually drawn.
          font: '500 28px/1.4 Inter, system-ui, sans-serif',
          color: '#8A8A8A',
        }}
      >
        This one could not be drawn.
      </div>
    );
  }
}
